import { use, useEffect, useRef } from 'react';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';

import { Connector } from 'features/connectors/domain/connector';
import { useGetConnectors } from 'features/connectors/domain/queries/connector-queries';
import { ReadConnectorsResponse } from 'features/connectors/domain/read-connectors-response';
import useConnectorDetails, { ConnectorDetailsHook } from 'features/connectors/hooks/connector-details-hook';
import useConnectorFilters, { ConnectorFiltersHook } from 'features/connectors/hooks/connector-filters-hook';

export interface ConnectorsHook {
  connectorsResponse?: ReadConnectorsResponse;
  isLoading: boolean;
  isSuccess: boolean;

  connectorDetailsHook: ConnectorDetailsHook;
  connectorsFilterHook: ConnectorFiltersHook;

  openDetailsInCreationMode: () => void;
  openDetailsInViewingModeAndRefresh: (connector: Connector) => void;
  openDetailsInEditingModeAndRefresh: (connector: Connector) => void;
  refresh: () => void;
}

const useConnectors = (): ConnectorsHook => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const { mutate: triggerReadConnectors, data: connectorsResponse, error, isError, isPending: isLoading, isSuccess } = useGetConnectors();

  const pendingSelectIdRef = useRef<string | undefined>(undefined);

  const connectorsFilterHook = useConnectorFilters(triggerReadConnectors);
  const refresh = () => {
    triggerReadConnectors(connectorsFilterHook.readConnectorsQuery);
    connectorsFilterHook.loadMonitorFilters();
  };

  const connectorDetailsHook = useConnectorDetails((connectorId?: string) => {
    pendingSelectIdRef.current = connectorId;
    triggerReadConnectors(connectorsFilterHook.readConnectorsQuery);
  });

  useEffect(() => {
    if (connectorsResponse && pendingSelectIdRef.current) {
      const id = pendingSelectIdRef.current;
      pendingSelectIdRef.current = undefined;
      const found = connectorsResponse.connectors.find((c) => c.id === id);
      if (found) {
        connectorDetailsHook.changeCurrentSelectedConnector(found);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectorsResponse]);

  useEffect(() => {
    if (isError && error) {
      setErrorMessage(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const openDetailsInCreationMode = () => {
    connectorDetailsHook.openDetailsInCreationMode();
  };

  const openDetailsInEditingModeAndRefresh = (connector: Connector) => {
    pendingSelectIdRef.current = connector.id;
    triggerReadConnectors(connectorsFilterHook.readConnectorsQuery);
    connectorDetailsHook.openDetailsInEditingMode(connector);
  };

  const openDetailsInViewingModeAndRefresh = (connector: Connector) => {
    pendingSelectIdRef.current = connector.id;
    triggerReadConnectors(connectorsFilterHook.readConnectorsQuery);
    connectorDetailsHook.openDetailsInViewingMode(connector);
  };

  return {
    connectorsResponse,
    isLoading,
    isSuccess,

    connectorDetailsHook,
    connectorsFilterHook,

    openDetailsInCreationMode,
    openDetailsInEditingModeAndRefresh,
    openDetailsInViewingModeAndRefresh,
    refresh,
  };
};

export default useConnectors;
